import React, { useState, useEffect, useCallback } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { walletService } from './services/walletService'
import { LanguageProvider } from './i18n'
import Navigation from './components/Navigation'
import Footer from './components/Footer'
import HomePage from './pages/HomePage'
import MiningPage from './pages/MiningPage'
import FuelPoolPage from './pages/FuelPoolPage'
import TeamPage from './pages/TeamPage'
import AssetPage from './pages/AssetPage'
import './styles/global.css'

function App() {
  const [isConnected, setIsConnected] = useState(false)
  const [userAddress, setUserAddress] = useState('')
  const [balance, setBalance] = useState(0)
  const [currentWalletType, setCurrentWalletType] = useState(null)

  useEffect(() => {
    initializeApp()
    setupWalletListeners()
    
    return () => {
      walletService.off('connect')
      walletService.off('disconnect')
      walletService.off('accountsChanged')
    }
  }, [])

  const initializeApp = async () => {
    try {
      await walletService.initialize()
    } catch (error) {
      console.error('应用初始化失败:', error)
    }
  }

  const setupWalletListeners = () => {
    walletService.on('connect', async (data) => {
      setIsConnected(true)
      setUserAddress(data.address)
      setCurrentWalletType(data.walletType)
      const userBalance = await walletService.getUserBalance()
      setBalance(userBalance)
    })

    walletService.on('disconnect', () => {
      setIsConnected(false)
      setUserAddress('')
      setBalance(0)
      setCurrentWalletType(null)
    })

    walletService.on('accountsChanged', async (address) => {
      setUserAddress(address)
      const userBalance = await walletService.getUserBalance()
      setBalance(userBalance)
    })
  }

  const updateBalance = useCallback(async () => {
    if (isConnected && walletService.userAddress) {
      const userBalance = await walletService.getUserBalance()
      setBalance(userBalance)
    }
  }, [isConnected])

  useEffect(() => {
    updateBalance()
  }, [isConnected, updateBalance])

  const connectWallet = async () => {
    try {
      setIsConnected(walletService.isConnected)
      setUserAddress(walletService.userAddress)
      setCurrentWalletType(walletService.currentWalletType)
      const userBalance = await walletService.getUserBalance()
      setBalance(userBalance)
    } catch (error) {
      console.error('连接钱包失败:', error)
      alert(error.message || '连接钱包失败，请重试')
    }
  }

  const disconnectWallet = () => {
    walletService.disconnect()
    setIsConnected(false)
    setUserAddress('')
    setBalance(0)
    setCurrentWalletType(null)
  }

  return (
    <LanguageProvider>
      <Router>
        <div className="main-container">
          {/* 导航栏 */}
          <Navigation
            isConnected={isConnected}
            userAddress={userAddress}
            balance={balance}
            currentWalletType={currentWalletType}
            onConnect={connectWallet}
            onDisconnect={disconnectWallet}
          />

          {/* 主内容 */}
          <main className="content-section">
            <Routes>
              <Route
                path="/"
                element={
                  <HomePage
                    isConnected={isConnected}
                    userAddress={userAddress}
                    balance={balance}
                    onConnect={connectWallet}
                    onDisconnect={disconnectWallet}
                  />
                }
              />
              <Route
                path="/mining"
                element={
                  <MiningPage
                    isConnected={isConnected}
                    userAddress={userAddress}
                  />
                }
              />
              <Route
                path="/fuel"
                element={
                  <FuelPoolPage
                    isConnected={isConnected}
                    userAddress={userAddress}
                  />
                }
              />
              <Route
                path="/team"
                element={
                  <TeamPage
                    isConnected={isConnected}
                    userAddress={userAddress}
                  />
                }
              />
              <Route
                path="/assets"
                element={
                  <AssetPage
                    isConnected={isConnected}
                    userAddress={userAddress}
                  />
                }
              />
            </Routes>
          </main>

          {/* 页脚 */}
          <Footer />
        </div>
      </Router>
    </LanguageProvider>
  )
}

export default App
